import { Checkbox, FormControlLabel } from '@mui/material';
import { SquareCheck } from 'lucide-react';

import { CheckboxFilterPanel } from '@modules/_shared/components/filters/quick-filters/CheckboxFilterPanel';
import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { useLocale } from '@shared/hooks/locale';

type ActivityStatus = 'pending' | 'successful' | 'cancelled';

const COMBOS: Record<ActivityStatus, Record<string, unknown>> = {
  pending: { done: { _eq: false } },
  successful: {
    done: { _eq: true },
    success: { _eq: true },
  },
  cancelled: {
    done: { _eq: true },
    success: { _eq: false },
  },
};

const classify = (entry: any): ActivityStatus | null => {
  if (entry == null || typeof entry !== 'object') {
    return null;
  }
  const keys = Object.keys(entry).sort().join(',');
  if (keys === 'done' && entry.done?._eq === false) {
    return 'pending';
  }
  if (keys !== 'done,success' && keys !== 'activity_type,done,success') {
    return null;
  }
  if (entry.done?._eq === true && entry.success?._eq === true) {
    return 'successful';
  }
  if (entry.done?._eq === true && entry.success?._eq === false) {
    return 'cancelled';
  }
  return null;
};

const isStatusGroup = (group: any): boolean =>
  group != null &&
  typeof group === 'object' &&
  Object.keys(group).length === 1 &&
  Array.isArray(group._or) &&
  group._or.length > 0 &&
  group._or.every((entry: any) => classify(entry) != null);

const readSelected = (where: any): Set<ActivityStatus> => {
  const selected = new Set<ActivityStatus>();
  const ands = Array.isArray(where?._and) ? where._and : [];
  const entries = ands.filter(isStatusGroup).flatMap((group: any) => group._or);
  for (const entry of entries) {
    const status = classify(entry);
    if (status) {
      selected.add(status);
    }
  }
  if (
    selected.size === 0 &&
    where?.done?._eq === false &&
    where?.success == null
  ) {
    selected.add('pending');
  }
  return selected;
};

type ActivityStatusFilterProps = {
  label: string;
  where: any;
  queryParamsScope?: string;
  disabled?: (where: {}) => boolean;
};

export const ActivityStatusFilter = ({
  label,
  where,
  queryParamsScope,
  disabled,
}: ActivityStatusFilterProps) => {
  const { t } = useLocale();
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);

  const selected = readSelected(where);

  const apply = (next: Set<ActivityStatus>) => {
    const newWhere = { ...where };
    delete newWhere.done;
    delete newWhere.success;
    const foreignAnds = (Array.isArray(where?._and) ? where._and : []).filter(
      (group: any) => !isStatusGroup(group),
    );
    const statusOrs = [...next].map(status => COMBOS[status]);
    const newAnds =
      statusOrs.length > 0 ? [...foreignAnds, { _or: statusOrs }] : foreignAnds;
    if (newAnds.length > 0) {
      newWhere._and = newAnds;
    } else {
      delete newWhere._and;
    }
    setFiltersParams(newWhere);
  };

  const toggle = (status: ActivityStatus, checked: boolean) => {
    const next = new Set(selected);
    if (checked) {
      next.add(status);
    } else {
      next.delete(status);
    }
    apply(next);
  };

  const handleDelete = () => {
    apply(new Set());
  };

  const options: Array<{ status: ActivityStatus; label: string }> = [
    { status: 'pending', label: t('Pending') },
    { status: 'successful', label: t('Successful') },
    { status: 'cancelled', label: t('Cancelled') },
  ];

  const hasActiveFilter = selected.size > 0;

  return (
    <FilterChip
      label={label}
      icon={<SquareCheck size={16} />}
      onDelete={hasActiveFilter ? handleDelete : undefined}
      disabled={disabled?.(where) ?? false}
      renderFilter={() => (
        <CheckboxFilterPanel>
          {options.map(option => (
            <FormControlLabel
              key={option.status}
              control={
                <Checkbox
                  size="small"
                  checked={selected.has(option.status)}
                  onChange={e => toggle(option.status, e.target.checked)}
                />
              }
              label={option.label}
              sx={{ mx: 0.5 }}
            />
          ))}
        </CheckboxFilterPanel>
      )}
    />
  );
};
